import { motion } from 'framer-motion'

import { useMyMonitoringTestControl } from '@apps-common/apis/my-monitoring/control'
import { useTestUnitControl } from '@apps-common/apis/portal/control'
import { ButtonRoundedSm } from '@apps-common/components/elements/button'
import { VertSpacerSm } from '@apps-common/components/layout'
import { ModalContainer } from '@apps-common/components/modals/modal'
import { CONTROL_TRIGGER_TYPE_LATCH } from '@apps-common/config/controls'
import { commonIcons } from '@apps-common/config/icons'
import { ModalDismiss } from '@apps-common/config/modal'
import { useProjectHook } from '@apps-common/hooks/useProjectHook'

export interface ITestOutputModalProps {
  unitId: string
  triggerType: string
  onTime: number
  outputNumber: string
  onCommand: string
  offCommand: string
}

export const TestOutputModal = ({
  onDismiss,
  componentProps
}: {
  onDismiss: (data?: string | null | undefined | number, role?: string) => void
  componentProps: ITestOutputModalProps
}) => {
  const { unitId, triggerType, onTime, outputNumber, onCommand, offCommand } = componentProps
  const useTestHook = useProjectHook(useTestUnitControl, useMyMonitoringTestControl)

  const { mutateAsync: mutateTestAsync, isPending: testIsPending } = useTestHook(true, () => onDismiss(null, ModalDismiss.Confirm))

  const testOutput = async (commandState: string) => {
    await mutateTestAsync({
      model: {
        unitId,
        triggerType,
        onTime,
        commands: [
          {
            id: 0,
            unitControlId: 0,
            commandState,
            command: outputNumber,
            value: commandState
          }
        ]
      }
    })
  }

  return (
    <ModalContainer
      innerClasses='sm:!min-h-[220px] !border-none !bg-transparent flex items-center justify-center shadow-none'
      containerClasses='!flex !items-center !justify-center'
    >
      <motion.div layout className='bg-surface border border-border text-text p-4 rounded-3xl'>
        <div className='w-[180px] flex flex-col mx-auto'>
          <div className='relative flex flex-col items-center'>
            <div className='w-[50px] h-[50px] rounded-full bg-surface-2 flex items-center justify-center'>
              <commonIcons.testOutput />
            </div>
            <VertSpacerSm />
            <p className='uppercase text-[0.8em]'>Output test</p>
          </div>
          <div className='border-t border-border w-full mx-auto my-6' />
          <div className='flex flex-col gap-[15px]'>
            <ButtonRoundedSm
              type='button'
              loading={testIsPending}
              icon={<commonIcons.controlOn />}
              className='action-success'
              callback={() => testOutput(onCommand)}
              text='Send On'
            />
            <ButtonRoundedSm
              type='button'
              loading={testIsPending || triggerType !== CONTROL_TRIGGER_TYPE_LATCH.value}
              icon={<commonIcons.controlOff />}
              className='action-error'
              callback={() => testOutput(offCommand)}
              text='Send Off'
            />
          </div>

          <div className='border-t border border-border w-full mx-auto my-6' />
          <ButtonRoundedSm
            type='button'
            loading={testIsPending}
            icon={<commonIcons.close />}
            className='action-neutral'
            callback={() => onDismiss(null, 'cancel')}
            text='Cancel'
          />
        </div>
      </motion.div>
    </ModalContainer>
  )
}
